import { createContext, useContext, useMemo, ReactNode } from 'react';
import { SnackbarOrigin } from '@mui/material';

export type NotificationType = 'error' | 'success' | 'info';

export interface NotificationInput {
    message: ReactNode;
    type: NotificationType;
    autoClose?: boolean;
    anchorOrigin?: SnackbarOrigin;
}

export interface Notification extends NotificationInput {
    id: string;
    closable: boolean;
}

export interface NotificationContextData {
    pushNotification(notification: NotificationInput): void;
}

export const NotificationContext = createContext<NotificationContextData | null>(null);

export function useNotifications() {
    const notificationContext = useContext(NotificationContext);
    if (!notificationContext) {
        throw new Error('useNotifications have to be called inside Notification provider');
    }

    // define shortcuts methods for the most common use-cases
    const { pushNotification } = notificationContext;
    const pushSuccessNotification = (message: ReactNode) => pushNotification({ message, type: 'success' });
    const pushErrorNotification = (message: ReactNode) => pushNotification({ message, type: 'error' });
    const pushApiErrorNotification = (message: ReactNode) =>
        pushErrorNotification(
            <>
                {message}
                <br />
                Please try again later.
            </>,
        );
    // note: no need to useCallback on the above methods, they have no mutable dependencies; notificationContext is stable
    return useMemo(
        () => ({ pushNotification, pushSuccessNotification, pushErrorNotification, pushApiErrorNotification }),
        [],
    );
}
